import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';

import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { useShortcuts } from '@/context/ShortcutContext';
import { useFullMode } from '@/hooks/useFullMode';

import type { ShortcutDefinition } from '@/context/ShortcutContext';

import { ROUTES } from '@/constants/routes';
import {
  MY_WORK_ITEMS,
  MANAGER_ITEMS,
  ADMIN_ITEMS,
  REPORT_ITEMS,
  PM_REPORT_ITEMS,
  filterNavItems,
} from '@/constants/navigation';
import type { NavItem } from '@/constants/navigation';

import { Loading } from '@/components/common/Loading';
import { ShortcutsOverlay } from '@/components/common/ShortcutsOverlay';
import { UserInfo } from '@/components/common/UserInfo';
import styles from './Layout.module.scss';

interface LayoutProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ isDarkMode, onToggleTheme }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useShortcuts();
  const isFullMode = useFullMode();
  const handleCloseShortcuts = useCallback(() => {
    setShortcutsOpen(false);
  }, []);

  const environmentTag = user?.environmentTag;
  const showManager = user?.isAdmin || user?.isProjectManager;

  const globalShortcuts = useMemo((): ShortcutDefinition[] => {
    const shortcuts: ShortcutDefinition[] = [
      {
        key: '?', scope: 'global',
        label: 'Shortcuts', description: 'Show keyboard shortcuts',
        handler: () => setShortcutsOpen(true),
      },
      {
        key: 't', sequence: 'g', scope: 'global',
        label: 'Go to Timesheet', description: 'Go to Timesheet',
        handler: () => navigate(ROUTES.TIMESHEET),
      },
      {
        key: 's', sequence: 'g', scope: 'global',
        label: 'Go to Submits', description: 'Go to My Submits',
        handler: () => navigate(ROUTES.MY_SUBMITS),
      },
    ];
    if (showManager) {
      shortcuts.push({
        key: 'a', sequence: 'g', scope: 'global',
        label: 'Go to Approvals', description: 'Go to Approvals',
        handler: () => navigate(ROUTES.APPROVALS),
      });
    }
    return shortcuts;
  }, [navigate, showManager]);

  useEffect(() => {
    return register(globalShortcuts);
  }, [register, globalShortcuts]);

  const drawerPaperClass = collapsed
    ? `${styles.layoutDrawerPaper} ${styles.layoutDrawerPaperCollapsed}`
    : `${styles.layoutDrawerPaper} ${styles.layoutDrawerPaperExpanded}`;

  const mainClass = collapsed
    ? `${styles.layoutMain} ${styles.layoutMainCollapsed}`
    : `${styles.layoutMain} ${styles.layoutMainExpanded}`;

  const renderNavSection = (items: NavItem[], label?: string): React.ReactNode => (
    <>
      {label && !collapsed && (
        <Typography className={styles.layoutSectionLabel}>
          {label}
        </Typography>
      )}
      {label && collapsed && <Divider />}
      <List className={styles.layoutNavList}>
        {items.map((item) => (
          <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
            <ListItemButton
              selected={location.pathname === item.path}
              aria-current={location.pathname === item.path ? 'page' : undefined}
              onClick={() => navigate(item.path)}
              className={styles.layoutNavItem}
            >
              <ListItemIcon className={styles.layoutNavIcon}>{item.icon}</ListItemIcon>
              {!collapsed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </>
  );

  const showAdmin = user?.isAdmin;

  return (
    <Box className={styles.layoutRoot}>
      <a href="#main-content" className={styles.layoutSkipLink}>Skip to main content</a>
      <Drawer
        variant="permanent"
        className={styles.layoutDrawer}
        PaperProps={{ className: drawerPaperClass }}
      >
        <Box className={styles.layoutDrawerHeader}>
          {!collapsed && (
            <Box className={styles.layoutLogoContainer} onClick={() => navigate(ROUTES.TIMESHEET)}>
              <img
                src={isDarkMode ? '/brimit-logo_transparent_white.png' : '/brimit-logo_transparent_dark.png'}
                alt="Brimit"
                className={styles.layoutLogo}
              />
              <Typography className={styles.layoutTitle}>
                Timesheet
              </Typography>
              {environmentTag && (
                <span className={styles.layoutEnvironmentTag}>{environmentTag}</span>
              )}
            </Box>
          )}
          <IconButton onClick={() => setCollapsed(!collapsed)} className={styles.layoutToggleButton} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <Divider />

        <Box component="nav" aria-label="Main navigation" className={styles.layoutNavContent}>
          {renderNavSection(filterNavItems(MY_WORK_ITEMS, isFullMode))}
          {isFullMode && renderNavSection(REPORT_ITEMS, 'Reports')}
          {isFullMode && showManager && renderNavSection(PM_REPORT_ITEMS)}
          {showManager && renderNavSection(filterNavItems(MANAGER_ITEMS, isFullMode), 'Manager')}
          {showAdmin && renderNavSection(filterNavItems(ADMIN_ITEMS, isFullMode), 'Admin')}
        </Box>

        <Box className={styles.layoutDrawerFooter}>
          <Divider />
          <Box className={styles.layoutUserSection}>
            <Tooltip title={collapsed ? 'Profile' : ''} placement="right">
              <ListItemButton
                selected={location.pathname === ROUTES.PROFILE}
                aria-current={location.pathname === ROUTES.PROFILE ? 'page' : undefined}
                onClick={() => navigate(ROUTES.PROFILE)}
                className={styles.layoutNavItem}
              >
                <UserInfo
                  name={user?.name ?? 'User'}
                  size={collapsed ? 'small' : 'medium'}
                  showEmail={!collapsed}
                />
              </ListItemButton>
            </Tooltip>
          </Box>
          <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'} placement="right">
            <IconButton onClick={onToggleTheme} className={styles.layoutThemeToggle} aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      <Box component="main" id="main-content" className={mainClass}>
        <Suspense key={location.pathname} fallback={<Loading />}>
          <Outlet context={{ isDarkMode, onToggleTheme, openShortcuts: () => setShortcutsOpen(true) }} />
        </Suspense>
      </Box>

      <ShortcutsOverlay open={shortcutsOpen} onClose={handleCloseShortcuts} />
    </Box>
  );
};
